import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ui/Toast';
import { apiService } from '../../services/apiService';
import { CREDIT_PACKAGES } from '../../constants';
import { ToolHeader } from '../../components/ui/ToolHeader';

export const PricingTool = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async (packageId: string) => {
    if (!user) return; 
    setLoading(true);
    try {
      const response = await apiService.createCheckout(user.uid, packageId);
      if (response.url) window.location.href = response.url;
    } catch (error: any) { 
        addToast("Checkout Error: " + error.message, 'error'); 
    } finally { 
        setLoading(false); 
    }
  };

  return (
      <div className="flex flex-col items-center p-6">
          <ToolHeader 
            title="Credits & Pricing"
            description={
              <>
                <p className="font-bold">Pay-As-You-Go. No monthly subscriptions.</p>
                <p>Credits never expire and remain in your account until used. Choose a package below to add credits instantly via secure Stripe checkout.</p>
              </>
            }
          />
          <div className="grid md:grid-cols-3 gap-8 w-full max-w-5xl items-center">
              {CREDIT_PACKAGES.map((pkg) => {
                  const isPro = pkg.id === 'pro';
                  return (
                      <div 
                        key={pkg.id} 
                        className={`
                            relative flex flex-col p-8 rounded-2xl border transition-all duration-300
                            ${isPro ? 'bg-white border-primary-500 shadow-2xl scale-105 z-10' : 'bg-white border-slate-200 shadow-sm hover:shadow-lg'}
                        `}
                      >
                          {isPro && (
                              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary-600 text-white px-4 py-1 rounded-full text-xs font-bold tracking-wider shadow-lg">
                                  MOST POPULAR
                              </div>
                          )}
                          
                          <div className="mb-4">
                              <h3 className="text-xl font-bold text-slate-900">{pkg.name}</h3>
                              <div className="text-sm text-slate-500 min-h-[40px] mt-1">{pkg.description}</div>
                          </div>

                          <div className="flex items-baseline gap-1 mb-6">
                              <span className="text-4xl font-extrabold text-slate-900">${pkg.price}</span>
                              <span className="text-slate-500 font-medium">USD</span>
                          </div>

                          <div className="mb-6">
                              <div className={`
                                  inline-block w-full text-center rounded-lg py-3 font-bold text-lg
                                  ${isPro ? 'bg-primary-50 text-primary-700 border border-primary-100' : 'bg-slate-50 text-slate-700 border border-slate-100'}
                              `}>
                                  {pkg.credits} Credits
                              </div>
                          </div>

                          <ul className="space-y-3 mb-8 flex-1">
                              {pkg.features.map((feature, i) => (
                                  <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                                      <CheckCircle size={16} className={`mt-0.5 shrink-0 ${isPro ? 'text-primary-500' : 'text-slate-400'}`} />
                                      <span>{feature}</span>
                                  </li>
                              ))}
                          </ul>

                          <Button 
                              onClick={() => handleCheckout(pkg.id)} 
                              isLoading={loading}
                              className={`w-full py-3 ${!isPro && 'bg-slate-900 hover:bg-slate-800'}`}
                          >
                              Buy {pkg.name}
                          </Button>
                      </div>
                  );
              })}
          </div>
      </div>
  );
};
